// src/layouts/configuration/components/pricingRules/index.js
import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';

// --- Material-UI & Argon Components ---
import {
  Card,
  Collapse,
  IconButton,
  Icon,
  Paper,
  Divider,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Checkbox,
  Grid,
} from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import { grey } from '@mui/material/colors';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import ArgonSelect from 'components/ArgonSelect';

// --- Contexts & Services ---
import { useVisibility } from 'context/visibilityContext';
import { useNotifications } from 'context/NotificationContext';

const renderFields = (fields, priceRulesFields) => {
  if (!fields) {
    return <ArgonTypography variant="caption">No details available.</ArgonTypography>;
  }
  if (!priceRulesFields || priceRulesFields.length === 0) {
    return <ArgonTypography variant="caption">No fields are configured for display.</ArgonTypography>;
  }

  return (
    <ArgonBox>
      {priceRulesFields.map((key) => {
        const value = fields[key];
        return (
          <ArgonBox key={key} mb={1} width="100%">
            <Grid container alignItems="center" spacing={0}>
              <Grid item xs={7}>
                <ArgonTypography
                  fontSize={12}
                  fontWeight="bold"
                  color="textSecondary"
                  align="left"
                  sx={{ wordWrap: 'break-word', color: grey[700] }}
                  ml={3}
                >
                  {key}
                </ArgonTypography>
              </Grid>
              <Grid item xs={1} style={{ textAlign: 'center' }}>
                <Divider orientation="vertical" flexItem />
              </Grid>
              <Grid item xs={4} style={{ textAlign: 'center' }}>
                <ArgonBox
                  bgcolor={grey[200]}
                  color={grey[700]}
                  p={0.8}
                  borderRadius="md"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <ArgonTypography
                    fontSize={12}
                    variant="body2"
                    fontWeight="bold"
                    sx={{ wordWrap: 'break-word', width: '100%' }}
                  >
                    {value === null || value === undefined ? 'N/A' : String(value)}
                  </ArgonTypography>
                </ArgonBox>
              </Grid>
            </Grid>
          </ArgonBox>
        );
      })}
    </ArgonBox>
  );
};

const PricingDataTable = ({ title, data, columns }) => {
  if (!data || data.length === 0 || !columns || columns.length === 0) {
    return null;
  }

  const getFieldValue = (item, field) => {
    if (field === 'SBQQ__Active__c' || field === 'SBQQ__ParentRuleIsActive__c') {
      const isActive = item.fields?.[field] === 'true' || item.fields?.[field] === true;
      return (
        <Tooltip title={isActive ? 'Active' : 'Inactive'}>
          {isActive ? (
            <CircleIcon color="success" sx={{ fontSize: 16, verticalAlign: 'middle' }} />
          ) : (
            <CircleIcon color="disabled" sx={{ fontSize: 16, verticalAlign: 'middle' }} />
          )}
        </Tooltip>
      );
    }

    const fields = item.fields || {};
    if (field.toLowerCase() === 'name' && item.name) return item.name;
    const value = fields[field];
    return value === null || value === undefined ? 'N/A' : String(value);
  };

  return (
    <ArgonBox mt={2}>
      <ArgonTypography variant="h6" fontWeight="medium" mb={1} ml={1}>
        {title}
      </ArgonTypography>
      <TableContainer component={Paper}>
        <Table size="small" aria-label={`${title.toLowerCase()} table`}>
          <TableHead sx={{ backgroundColor: grey[200], display: 'contents' }}>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col}>
                  <ArgonTypography variant="caption" fontWeight="bold">
                    {col
                      .replace(/SBQQ__/g, '')
                      .replace(/__c/g, '')
                      .replace(/([A-Z])/g, ' $1')
                      .trim()}
                  </ArgonTypography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => (
              <TableRow key={item.id || item.sfId || index}>
                {columns.map((col) => (
                  <TableCell key={col}>{getFieldValue(item, col)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </ArgonBox>
  );
};

// --- MODIFICATION: Accept `orgType` prop ---
function PricingRules({ orgs, loading, priceRules, selectedOrgId, orgType }) {
  const { visibilityConfig } = useVisibility();
  const { addNotification } = useNotifications();

  const [selectedOrg, setSelectedOrg] = useState(null);
  const [expandedRule, setExpandedRule] = useState({});
  const [selectedRules, setSelectedRules] = useState([]);

  const orgOptions = useMemo(() => {
    if (!orgs) return [];
    return orgs.map((org) => ({
      label: org.name,
      value: org.instance_url,
      orgData: org,
    }));
  }, [orgs]);

  // --- MODIFICATION: Use `orgType` to select the correct config section ---
  const { priceRulesFields, priceConditionFields, priceActionFields, lookupQueryFields } = useMemo(() => {
    // `orgType` will be 'cpq' or 'rc'
    const configSection = visibilityConfig[orgType] || [];
    console.log('[pricingRules] visibilityConfig', visibilityConfig);

    console.log('[pricingRules] configSection', configSection);

    const findFields = (objectName) => {
      const config = configSection.find((obj) => obj.objectName === objectName);
      return config?.displayColumns
        ? config.displayColumns
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : [];
    };
    return {
      priceRulesFields: findFields('SBQQ__PriceRule__c'),
      priceConditionFields: findFields('SBQQ__PriceCondition__c'),
      priceActionFields: findFields('SBQQ__PriceAction__c'),
      lookupQueryFields: findFields('SBQQ__LookupQuery__c'),
    };
  }, [visibilityConfig, orgType]); // Add orgType to dependency array
  // --- END MODIFICATION ---

  const handleChangeOrg = (event) => {
    const { value } = event.target;
    const selectedOrgOption = orgOptions.find((orgOpt) => orgOpt.value === value);
    setSelectedOrg(selectedOrgOption || null);
  };

  const handleRuleSelection = (event, ruleId) => {
    event.stopPropagation();
    if (selectedRules.includes(ruleId)) {
      setSelectedRules(selectedRules.filter((id) => id !== ruleId));
    } else {
      setSelectedRules([...selectedRules, ruleId]);
    }
  };

  const handleDeploy = () => {
    const rulesToDeploy = priceRules.filter((rule) => selectedRules.includes(rule.id));
    addNotification({
      title: ['Deployment Action'],
      details: `Deploying ${rulesToDeploy.length} pricing rule(s) to ${selectedOrg.label}. (This is a placeholder).`,
      severity: 'info',
    });
    console.log('Deploying these rules:', rulesToDeploy);
  };

  const handleRuleClick = (ruleId) => {
    setExpandedRule((prev) => ({ ...prev, [ruleId]: !prev[ruleId] }));
  };

  if (!selectedOrgId) {
    return (
      <Card>
        <ArgonBox p={3} textAlign="center">
          <ArgonTypography variant="h6" color="text">
            Please select an organization to view its pricing rules.
          </ArgonTypography>
        </ArgonBox>
      </Card>
    );
  }

  if (!loading && (!priceRules || priceRules.length === 0)) {
    return (
      <Card>
        <ArgonBox p={3} textAlign="center">
          <ArgonTypography variant="h6" color="text">
            No pricing rules found for the selected organization.
          </ArgonTypography>
        </ArgonBox>
      </Card>
    );
  }

  return (
    <ArgonBox>
      <Card sx={{ mb: 2 }}>
        <ArgonBox p={2} display="flex" justifyContent="space-between" alignItems="center">
          <ArgonTypography variant="h6">Deployment Target</ArgonTypography>
          <ArgonBox display="flex" alignItems="center" gap={1}>
            <ArgonSelect
              name="orgPlatform"
              placeholder="Select Org to Deploy"
              value={selectedOrg ? selectedOrg.value : ''}
              onChange={handleChangeOrg}
              options={orgOptions || []}
              sx={{ minWidth: '250px' }}
            />
            <Button
              variant="contained"
              color="info"
              onClick={handleDeploy}
              disabled={!selectedOrg || selectedRules.length === 0 || loading}
            >
              {loading ? 'Loading...' : `Deploy (${selectedRules.length})`}
            </Button>
          </ArgonBox>
        </ArgonBox>
      </Card>

      {priceRules.map((rule) => {
        const ruleId = rule.id;
        const isSelected = selectedRules.includes(ruleId);

        const priceConditionsData = rule.priceConditions?.map((c) => c.priceConditions).filter(Boolean) || [];
        const priceActionsData = rule.priceActions?.map((a) => a.priceActions).filter(Boolean) || [];
        const lookupQueriesData = rule.lookupQueries?.map((q) => q.lookupQuery).filter(Boolean) || [];

        return (
          <React.Fragment key={ruleId}>
            <Card sx={{ mb: 1 }}>
              <ArgonBox
                p={1}
                borderRadius="md"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                onClick={() => handleRuleClick(ruleId)}
                sx={{ cursor: 'pointer' }}
                mt={0.5}
                mb={0.5}
              >
                <ArgonBox display="flex" alignItems="center">
                  <Checkbox
                    checked={isSelected}
                    onChange={(e) => handleRuleSelection(e, ruleId)}
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                  />
                  <ArgonTypography variant="h6" fontWeight="medium" color="secondary" ml={1}>
                    {rule.name}
                  </ArgonTypography>
                </ArgonBox>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRuleClick(ruleId);
                  }}
                >
                  <Icon>{expandedRule[ruleId] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
                </IconButton>
              </ArgonBox>
              <Collapse in={expandedRule[ruleId]} timeout="auto" unmountOnExit>
                <ArgonBox p={2}>
                  {renderFields(rule.fields, priceRulesFields)}
                  <Divider sx={{ my: 2 }} />

                  <PricingDataTable
                    title="Price Conditions"
                    data={priceConditionsData}
                    columns={priceConditionFields}
                  />
                  <PricingDataTable title="Price Actions" data={priceActionsData} columns={priceActionFields} />
                  <PricingDataTable title="Lookup Queries" data={lookupQueriesData} columns={lookupQueryFields} />
                </ArgonBox>
              </Collapse>
            </Card>
          </React.Fragment>
        );
      })}
    </ArgonBox>
  );
}

// --- MODIFICATION: Add `orgType` to propTypes ---
PricingRules.propTypes = {
  orgs: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  priceRules: PropTypes.array,
  selectedOrgId: PropTypes.string,
  orgType: PropTypes.string,
};

PricingRules.defaultProps = {
  priceRules: [],
  selectedOrgId: '',
  orgType: 'cpq', // Default value
};
// --- END MODIFICATION ---

export default PricingRules;
